import { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import {
	MERCH_CIRCUIT_CODE,
	RESOLUTION,
	WEBSITE_TUNNEL_LABELS,
	WEBSITE_TUNNEL_TYPES,
} from "app/constants";
import { FormattedMessage } from "react-intl";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import "./WebsiteTunnelSwitcher.scss";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import AppGlobalsContext from "app/AppGlobalsContext";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import IconHolidays from "app/pages/.shared/static/icons/WebsiteTunnelTypes/IconHolidays";
import IconStopover from "app/pages/.shared/static/icons/WebsiteTunnelTypes/IconStopover";
import IconHotelOnly from "app/pages/.shared/static/icons/WebsiteTunnelTypes/IconHotelOnly";
import IconCircuit from "app/pages/.shared/static/icons/WebsiteTunnelTypes/IconCircuit";
import {
	AsyncHotelOnlySearchFormContainer,
	AsyncSmartDPSearchFormContainer,
	AsyncStopoverSearchFormContainer,
} from "app/routes";
import env from "app/utils/env";

const WEBSITE_TUNNEL_ICONS = {
	[WEBSITE_TUNNEL_TYPES.HOLIDAYS]: IconHolidays,
	[WEBSITE_TUNNEL_TYPES.STOPOVER]: IconStopover,
	[WEBSITE_TUNNEL_TYPES.HOTEL_ONLY]: IconHotelOnly,
	[WEBSITE_TUNNEL_TYPES.MERCH_CIRCUIT]: IconCircuit,
};

const getTunnelLabelId = (tunnelType, hasSingleStopoverCity) => {
	if (tunnelType === WEBSITE_TUNNEL_TYPES.STOPOVER && hasSingleStopoverCity) {
		return "root.tab.label.stopover.at.city";
	}

	return WEBSITE_TUNNEL_LABELS[tunnelType];
};

const WebsiteTunnelSwitcher = ({
	websiteTunnels = [],
	stopoverCities,
	disableFormPersistance = true,
}) => {
	const navigate = useNavigate();
	const { shop, resolution } = useContext(AppGlobalsContext);
	const tabRefs = useRef([]);
	const [tabUnderlineWidth, setTabUnderlineWidth] = useState(0);
	const [tabUnderlineLeft, setTabUnderlineLeft] = useState(0);

	const { track } = useAnalytics();

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const hasMultipleTunnels = websiteTunnels?.length > 1;
	const [selectedIndex, setSelectedIndex] = useState(0);
	const doubleTab = websiteTunnels?.length === 2;
	const hasSingleStopoverCity = stopoverCities?.length === 1;

	const switcherClass = classNames("website-tunnel-switcher", {
		"website-tunnel-switcher--multiple": hasMultipleTunnels,
		"website-tunnel-switcher--double-tabs": doubleTab,
	});

	const switcherRef = useRef(null);
	const [isAutoScrollActive, setIsAutoScrollActive] = useState(false);

	useEffect(() => {
		const currentTabElement = tabRefs.current[selectedIndex];
		if (currentTabElement) {
			const currentRect = currentTabElement.getBoundingClientRect();
			const parentRect = switcherRef.current.getBoundingClientRect();
			setTabUnderlineLeft(currentRect.left - parentRect.left);
			setTabUnderlineWidth(currentTabElement?.clientWidth ?? 0);
		}
	}, [selectedIndex]);

	const handleFieldFocus = useCallback(() => {
		const fieldElement = switcherRef?.current;
		if (fieldElement) {
			setIsAutoScrollActive(true);
			const { top } = fieldElement.getBoundingClientRect();
			const currentScroll = window.scrollY;

			const offsetTop = currentScroll + top - 96;

			window.scrollTo({
				top: offsetTop,
				behavior: "smooth",
			});

			setTimeout(() => {
				setIsAutoScrollActive(false);
			}, 300);
		}
	}, [switcherRef?.current]);

	const handleTabSelect = index => {
		const selectedTunnel = websiteTunnels[index];

		track("click_website_tunnel_tab", {
			type: selectedTunnel?.websiteTunnelType,
		});

		if (selectedTunnel?.websiteTunnelType === WEBSITE_TUNNEL_TYPES.MERCH_CIRCUIT) {
			if (isMobile) {
				navigate({
					pathname: "/merch",
					search: `?code=${MERCH_CIRCUIT_CODE}`,
				});
			} else {
				// eslint-disable-next-line no-unused-expressions
				window?.open(
					`${env("BASE_URL")}/${shop}/merch?code=${MERCH_CIRCUIT_CODE}`,
					"_blank"
				);
			}
		} else {
			setSelectedIndex(index);
		}
	};

	return (
		<div className={switcherClass} ref={switcherRef}>
			<Tabs selectedIndex={selectedIndex} onSelect={handleTabSelect}>
				{hasMultipleTunnels && (
					<TabList>
						<span
							className="website-tunnel-switcher__active-line"
							style={{ left: tabUnderlineLeft, width: tabUnderlineWidth }}
						/>
						<span className="website-tunnel-switcher__border-line" />
						{websiteTunnels?.map((tunnel = {}, index) => {
							const websiteTunnelLabelId = getTunnelLabelId(
								tunnel.websiteTunnelType,
								hasSingleStopoverCity
							);
							const isActive = index === selectedIndex;
							const WebsiteTunnelIcon =
								WEBSITE_TUNNEL_ICONS[tunnel?.websiteTunnelType];
							return (
								<Tab key={index}>
									<div
										className={classNames("website-tunnel-switcher__tab", {
											"website-tunnel-switcher__tab--selected": isActive,
										})}
										ref={el => (tabRefs.current[index] = el)}
									>
										{WebsiteTunnelIcon && (
											<WebsiteTunnelIcon
												className={classNames(
													"website-tunnel-switcher__icon",
													{
														"website-tunnel-switcher__icon--active":
															isActive,
													}
												)}
											/>
										)}
										<Typography
											variant={TYPOGRAPHY_VARIANTS.REGULAR}
											isBold
											className="website-tunnel-switcher__label"
										>
											<FormattedMessage
												id={websiteTunnelLabelId}
												values={
													hasSingleStopoverCity
														? { city: stopoverCities[0].label }
														: undefined
												}
											/>
										</Typography>
									</div>
								</Tab>
							);
						})}
					</TabList>
				)}
				{websiteTunnels?.map((tunnel = {}, index) => (
					<TabPanel key={index}>
						{tunnel?.websiteTunnelType === WEBSITE_TUNNEL_TYPES.STOPOVER && (
							<AsyncStopoverSearchFormContainer
								disableFormPersistance={disableFormPersistance}
								onFieldFocus={handleFieldFocus}
								stopoverCities={stopoverCities}
								isAutoScrollActive={isAutoScrollActive}
							/>
						)}
						{tunnel?.websiteTunnelType === WEBSITE_TUNNEL_TYPES.HOLIDAYS && (
							<AsyncSmartDPSearchFormContainer
								disableFormPersistance={disableFormPersistance}
								onFieldFocus={handleFieldFocus}
								isAutoScrollActive={isAutoScrollActive}
							/>
						)}
						{tunnel?.websiteTunnelType === WEBSITE_TUNNEL_TYPES.HOTEL_ONLY && (
							<AsyncHotelOnlySearchFormContainer
								disableFormPersistance={disableFormPersistance}
								onFieldFocus={handleFieldFocus}
								isAutoScrollActive={isAutoScrollActive}
							/>
						)}
					</TabPanel>
				))}
			</Tabs>
		</div>
	);
};

WebsiteTunnelSwitcher.propTypes = {
	websiteTunnels: PropTypes.array,
	stopoverCities: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string,
			code: PropTypes.number,
		})
	),
	disableFormPersistance: PropTypes.bool,
};

export default memo(WebsiteTunnelSwitcher);
